import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/features/auth/context/AuthContext";
import { useTranslation } from "react-i18next";
import { planNameToKey } from "@/mocks/plans";
import { formatPhoneDisplay } from "@/utils/phone";
import LanguageSwitcher from "./LanguageSwitcher";
import BrandLogo from "@/shared/common/BrandLogo";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const { t } = useTranslation("common");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
    setMobileOpen(false);
  };

  const isLight = isHome ? scrolled : true;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isHome
          ? scrolled
            ? "bg-background-50/95 backdrop-blur-md shadow-sm"
            : "bg-transparent"
          : "bg-background-50/95 backdrop-blur-md shadow-sm border-b border-background-200/50"
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            className="flex items-center gap-2.5 group"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <BrandLogo className="w-8 h-8 md:w-9 md:h-9 text-primary-500 transition-transform group-hover:scale-105" />
            <span className={`font-heading text-xl md:text-2xl font-bold tracking-tight transition-colors ${isLight ? "text-foreground-950" : "text-white"}`}>
              Wyzon
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8 lg:gap-10">
            {isHome && (
              <>
                {[
                  { label: t("nav.advantages"), id: "advantages" },
                  { label: t("nav.industries"), id: "industries" },
                  { label: t("nav.testimonials"), id: "testimonials" },
                  { label: t("nav.pricing"), id: "pricing", href: "/plans" },
                  { label: t("nav.faq"), id: "faq" },
                  { label: t("nav.contact"), id: "contact" },
                ].map((item) =>
                  item.href ? (
                    <Link
                      key={item.id}
                      to={item.href}
                      className={`text-sm font-medium transition-colors hover:opacity-80 ${
                        isLight ? "text-foreground-700" : "text-white/90"
                      }`}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <button
                      key={item.id}
                      onClick={() => scrollTo(item.id)}
                      className={`text-sm font-medium transition-colors hover:opacity-80 cursor-pointer ${
                        isLight ? "text-foreground-700" : "text-white/90"
                      }`}
                    >
                      {item.label}
                    </button>
                  )
                )}
              </>
            )}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <LanguageSwitcher />
            {isAuthenticated ? (
              <Link
                to="/dashboard"
                className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-background-100 border border-background-200 hover:border-primary-500 hover:bg-background-200/60 transition-all cursor-pointer shadow-sm group"
                title={t("nav.dashboard", { defaultValue: "Dashboard" })}
              >
                <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center text-xs font-semibold text-primary-700 group-hover:bg-primary-500 group-hover:text-white transition-colors">
                  {user?.phone?.slice(-2) || "U"}
                </div>
                <span className="text-sm text-foreground-800 font-medium" dir="ltr">{user?.phone || "User"}</span>
              </Link>
            ) : (
              <Link
                to="/login"
                className={`text-sm font-medium px-5 py-2.5 rounded-full transition-all whitespace-nowrap ${
                  isHome
                    ? isLight
                      ? "bg-primary-500 text-white hover:bg-primary-600"
                      : "bg-white/20 text-white hover:bg-white/30"
                    : "bg-primary-500 text-white hover:bg-primary-600"
                }`}
              >
                {t("nav.signIn")}
              </Link>
            )}
          </div>

          <div className="md:hidden flex items-center gap-1">
            <LanguageSwitcher />
            <button
              className="w-10 h-10 flex items-center justify-center cursor-pointer"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={t("nav.toggleMenu")}
            >
              <i className={`ri-menu-line text-xl ${isLight ? "text-foreground-950" : "text-white"}`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50/98 backdrop-blur-lg border-t border-background-200/50 px-4 py-6 space-y-3">
          {isHome && (
            <>
              {[
                { label: t("nav.advantages"), id: "advantages" },
                { label: t("nav.industries"), id: "industries" },
                { label: t("nav.testimonials"), id: "testimonials" },
                { label: t("nav.pricing"), id: "pricing", href: "/plans" },
                { label: t("nav.faq"), id: "faq" },
                { label: t("nav.contact"), id: "contact" },
              ].map((item) =>
                item.href ? (
                  <Link
                    key={item.id}
                    to={item.href}
                    className="block w-full text-left text-foreground-700 hover:text-foreground-950 text-base font-medium py-2"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    key={item.id}
                    onClick={() => scrollTo(item.id)}
                    className="block w-full text-left text-foreground-700 hover:text-foreground-950 text-base font-medium py-2 cursor-pointer"
                  >
                    {item.label}
                  </button>
                )
              )}
            </>
          )}
          {isAuthenticated ? (
            <div className="pt-2 border-t border-background-200/50 space-y-2">
              <Link
                to="/dashboard"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-between p-3 rounded-2xl bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-xs font-semibold text-primary-700">
                    {user?.phone?.slice(-2) || "U"}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950" dir="ltr">{user?.phone}</p>
                    {user?.plan && (
                      <p className="text-xs text-primary-700">{t(planNameToKey(user.plan), { ns: "common" })}</p>
                    )}
                  </div>
                </div>
                <span className="text-xs font-medium text-primary-600 flex items-center gap-1">
                  {t("nav.dashboard")}
                  <i className="ri-arrow-right-line rtl:rotate-180"></i>
                </span>
              </Link>
              <div className="pt-1 px-1">
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    logout();
                  }}
                  className="w-full text-left rtl:text-right text-red-600 text-sm font-medium py-2 px-2 hover:bg-red-50 rounded-lg cursor-pointer"
                >
                  {t("nav.logout")}
                </button>
              </div>
            </div>
          ) : (
            <Link
              to="/login"
              className="block w-full text-center bg-primary-500 text-white text-sm font-medium px-5 py-2.5 rounded-full mt-3"
            >
              {t("nav.signIn")}
            </Link>
          )}

        </div>
      )}
    </nav>
  );
}
